import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { SERVER_BASE_URL } from '../../utils/api';
import DashboardHeader from '../../components/dashboard/DashboardHeader';
import DashboardFooter from '../../components/dashboard/DashboardFooter';
import { 
  FaShoppingBag, 
  FaCreditCard, 
  FaMapMarkerAlt, 
  FaUser, 
  FaHashtag,
  FaChevronDown,
  FaChevronUp,
  FaTruck,
  FaStore,
  FaBarcode
} from 'react-icons/fa';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px 60px 20px;
  width: 100%;
  
  @media (max-width: 1200px) {
    padding: 40px 20px 60px 20px;
  }
  
  @media (max-width: 768px) {
    padding: 20px 15px 40px 15px;
  }
`;
const OrderBlock = styled.div`
  background: white;
  border-radius: 16px;
  margin-bottom: 20px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  padding: 28px;
  border: 1px solid #f0f0f0;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 30px rgba(0,0,0,0.1);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    padding: 24px 20px;
    margin-bottom: 16px;
    border-radius: 12px;
  }
`;
const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f5f5f5;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 12px;
    padding-bottom: 8px;
  }
`;

const OrderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const OrderDate = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #34495e;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 8px;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const OrderIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #27ae60;
  background: #e8f5e8;
  border-radius: 6px;
  font-size: 0.8rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
    font-size: 0.7rem;
  }
`;

const OrderAmountContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: right;
  
  @media (max-width: 768px) {
    text-align: left;
    flex-direction: row;
  }
`;

const OrderAmount = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  color: #27ae60;
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;
const OrderDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    margin-bottom: 12px;
  }
`;

const OrderDetailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.95rem;
  color: #2c3e50;
  padding: 8px 0;
  flex: 1;
  min-width: 200px;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    gap: 10px;
    padding: 6px 0;
    min-width: auto;
  }
`;

const DetailIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #27ae60;
  flex-shrink: 0;
  background: #e8f5e8;
  border-radius: 8px;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    width: 22px;
    height: 22px;
    font-size: 0.8rem;
  }
`;

const DetailText = styled.span`
  flex: 1;
  word-break: break-word;
  line-height: 1.5;
  font-weight: 400;
`;

const OrderId = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 8px;
  
  @media (max-width: 768px) {
    justify-content: flex-start;
    margin-top: 12px;
  }
`;

const OrderIdIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #27ae60;
  background: #e8f5e8;
  border-radius: 6px;
  font-size: 0.8rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    width: 18px;
    height: 18px;
    font-size: 0.7rem;
  }
`;

const OrderIdText = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  
  @media (max-width: 768px) {
    gap: 2px;
  }
`;

const OrderIdValue = styled.span`
  font-size: 1rem;
  color: #2c3e50;
  font-weight: 600;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const PaidBadge = styled.span`
  background: #27ae60;
  color: #fff;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(39, 174, 96, 0.3);
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.8rem;
  }
`;

const PendingBadge = styled.span`
  background: #f39c12;
  color: #fff;
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(243, 156, 18, 0.3);
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.8rem;
  }
`;

const PayOrderButton = styled.button`
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  border: none;
  border-radius: 20px;
  padding: 8px 18px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: 768px) {
    padding: 6px 14px;
    font-size: 0.8rem;
  }
`;

const StatusBadge = styled.span`
  background: ${props => {
    switch (props.status) {
      case 'ready_for_pickup':
        return '#dcfce7';
      case 'shipped':
        return '#cffafe';
      case 'delivered':
        return '#dcfce7';
      case 'cancelled':
        return '#fee2e2';
      default:
        return '#fef3c7';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'ready_for_pickup':
        return '#166534';
      case 'shipped':
        return '#0e7490';
      case 'delivered':
        return '#166534';
      case 'cancelled':
        return '#991b1b';
      default:
        return '#92400e';
    }
  }};
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 0.85rem;
  font-weight: 600;
  white-space: nowrap;
  margin-left: 8px;
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.75rem;
    margin-left: 4px;
  }
`;

const ToggleButton = styled.button`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  color: #27ae60;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  
  &:hover {
    background: #e8f5e8;
    border-color: #27ae60;
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.8rem;
  }
`;

const ProductsList = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
`;

const ProductItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #e9ecef;
  
  &:last-child {
    border-bottom: none;
  }
`;

const ProductName = styled.span`
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  color: #2c3e50;
  flex: 1;
  min-width: 0;
`;

const ProductQuantity = styled.span`
  color: #95a5a6;
  font-size: 0.9rem;
  white-space: nowrap;
  flex-shrink: 0;
`;

const ProductPrice = styled.span`
  font-weight: 600;
  color: #27ae60;
  font-size: 1rem;
`;

const PriceSummary = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 16px 20px;
  margin-top: 16px;
  border: 1px solid #dee2e6;
`;

const PriceSummaryTitle = styled.div`
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  font-size: 0.9rem;
  
  &:last-child {
    border-top: 1px solid #dee2e6;
    margin-top: 8px;
    padding-top: 12px;
    font-weight: 600;
    font-size: 1rem;
  }
`;

const PriceLabel = styled.span`
  color: #6c757d;
`;

const PriceValue = styled.span`
  color: ${props => props.color || '#2c3e50'};
  font-weight: ${props => props.bold ? '600' : '400'};
`;

// Тестовые данные удалены - теперь загружаем реальные данные из API

const ORDERS_PER_PAGE = 5;

const OrdersHistory = () => {
  const { isAuthenticated } = useAuth();
  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [payingOrderId, setPayingOrderId] = useState(null);

  // Оплата существующего заказа
  const handlePayOrder = async (orderId) => {
    try {
      setPayingOrderId(orderId);
      const token = localStorage.getItem('token');
      const response = await fetch(`${SERVER_BASE_URL}/api/orders/${orderId}/robokassa/payment-url`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (response.ok && data.success && data.data?.paymentUrl) {
        // Сохраняем pendingOrder для страницы успешной оплаты
        localStorage.setItem('pendingOrder', JSON.stringify({
          orderId: data.data.orderId,
          orderNumber: data.data.orderNumber,
          amount: data.data.amount
        }));
        
        // Если URL слишком длинный — отправляем POST-форму вместо GET-редиректа
        if (data.data.usePostForm && data.data.formParams) {
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = data.data.paymentUrl;
          form.style.display = 'none';
          Object.entries(data.data.formParams).forEach(([key, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = value;
            form.appendChild(input);
          });
          document.body.appendChild(form);
          form.submit();
        } else {
          window.location.href = data.data.paymentUrl;
        }
      } else {
        alert(data.message || 'Ошибка при получении ссылки на оплату');
        setPayingOrderId(null);
      }
    } catch (error) {
      console.error('Ошибка оплаты:', error);
      alert('Произошла ошибка. Попробуйте ещё раз.');
      setPayingOrderId(null);
    }
  };

  // Функция для переключения раскрытия заказа
  const toggleOrderExpansion = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  // Загружаем заказы пользователя
  useEffect(() => {
    const loadOrders = async () => {
      if (!isAuthenticated()) return;

      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const response = await fetch(`${SERVER_BASE_URL}/api/orders?page=${page}&limit=${ORDERS_PER_PAGE}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setOrders(data.data?.orders || []);
          setTotalPages(data.data?.pagination?.pages || 0);
        } else {
          console.error('Failed to load orders:', response.status);
        }
      } catch (error) {
        console.error('Error loading orders:', error);
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [isAuthenticated, page]);

  if (loading) {
    return (
      <>
        <DashboardHeader />
        <Container>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h2>Загрузка заказов...</h2>
          </div>
        </Container>
        <DashboardFooter />
      </>
    );
  }

  return (
    <>
      <DashboardHeader />
      <Container>
        {orders.length > 0 ? (
          <>
            {orders.map(order => {
              const orderDate = new Date(order.createdAt).toLocaleString('ru-RU', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });

              return (
                <OrderBlock key={order.id}>
                  <OrderHeader>
                    <OrderTitle>
                      <OrderDate>
                        <OrderIcon>
                          <FaShoppingBag />
                        </OrderIcon>
                        Заказ от {orderDate}
                      </OrderDate>
                      {order.paymentStatus === 'paid' ? (
                        <PaidBadge>Оплачен</PaidBadge>
                      ) : (
                        <>
                          <PendingBadge>Ожидает оплаты</PendingBadge>
                          <PayOrderButton
                            onClick={(e) => { e.stopPropagation(); handlePayOrder(order.id); }}
                            disabled={payingOrderId === order.id}
                          >
                            {payingOrderId === order.id ? 'Загрузка...' : 'Оплатить'}
                          </PayOrderButton>
                        </>
                      )}
                      <StatusBadge status={order.status}>
                        {(() => {
                          const statusMap = {
                            'pending': 'Новый',
                            'processing': 'В обработке',
                            'shipped': 'Отправлен',
                            'ready_for_pickup': 'Готов к выдаче',
                            'delivered': 'Доставлен',
                            'cancelled': 'Отменен'
                          };
                          return statusMap[order.status] || order.status;
                        })()}
                      </StatusBadge>
                    </OrderTitle>
                    <OrderAmountContainer>
                      <OrderAmount>{order.totalAmount.toLocaleString()} ₽</OrderAmount>
                    </OrderAmountContainer>
                  </OrderHeader>
                  
                  <OrderDetails>
                    <OrderDetailRow>
                      <DetailIcon>
                        <FaCreditCard />
                      </DetailIcon>
                      <DetailText>
                        {order.paymentMethod === 'cash' ? 'Наличные' : 'Оплата банковской картой'}
                      </DetailText>
                    </OrderDetailRow>
                    
                    {order.deliveryMethodName && (
                      <OrderDetailRow>
                        <DetailIcon>
                          {order.isPickup ? <FaStore /> : <FaTruck />}
                        </DetailIcon>
                        <DetailText>
                          <strong>Доставка:</strong> {order.deliveryMethodName}
                          {order.pickupPointName && ` - ${order.pickupPointName}`}
                        </DetailText>
                      </OrderDetailRow>
                    )}
                    
                    
                    <OrderDetailRow>
                      <DetailIcon>
                        <FaMapMarkerAlt />
                      </DetailIcon>
                      <DetailText>
                        {order.isPickup && order.pickupPointAddress ? (
                          <>
                            <strong>Адрес пункта самовывоза:</strong> {order.pickupPointAddress}
                            {order.pickupPointWorkingHours && (
                              <> · <span style={{ color: '#95a5a6', fontSize: '0.9rem' }}>{order.pickupPointWorkingHours}</span></>
                            )}
                          </>
                        ) : (
                          <>
                        {order.shippingAddress1 || 'Адрес не указан'}, {order.shippingCity || 'Город не указан'}
                          </>
                        )}
                      </DetailText>
                    </OrderDetailRow>
                    
                    <OrderDetailRow>
                      <DetailIcon>
                        <FaUser />
                      </DetailIcon>
                      <DetailText>
                        {order.shippingFirstName || ''} {order.shippingLastName || ''} · {order.shippingPhone || ''}
                      </DetailText>
                    </OrderDetailRow>
                    
                  </OrderDetails>
                  
                  <OrderId>
                    <OrderIdIcon>
                      <FaHashtag />
                    </OrderIdIcon>
                    <OrderIdText>
                      <OrderIdValue>{order.orderNumber}</OrderIdValue>
                      {order.trackingNumber && (
                        <>
                          {' · '}
                          <FaBarcode style={{ marginLeft: '8px', marginRight: '4px' }} />
                          <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>Трек номер: {order.trackingNumber}</span>
                        </>
                      )}
                    </OrderIdText>
                  </OrderId>
                  
                  {/* Детализация стоимости заказа */}
                  <PriceSummary>
                    <PriceSummaryTitle>
                      💰 Детализация стоимости
                    </PriceSummaryTitle>
                    
                    <PriceRow>
                      <PriceLabel>Товары (исходная стоимость)</PriceLabel>
                      <PriceValue>{(order.subtotal || order.totalAmount).toLocaleString()} ₽</PriceValue>
                    </PriceRow>
                    
                    {order.discountAmount > 0 && (
                      <PriceRow>
                        <PriceLabel>
                          Скидка{order.discountReason && order.discountReason.match(/(\d+)%/) ? ` ${order.discountReason.match(/(\d+)%/)[1]}%` : ''}
                        </PriceLabel>
                        <PriceValue color="#27ae60">-{order.discountAmount.toLocaleString()} ₽</PriceValue>
                      </PriceRow>
                    )}
                    
                    {order.pointsSpent > 0 && (
                      <PriceRow>
                        <PriceLabel>Оплачено баллами</PriceLabel>
                        <PriceValue color="#ff6b35">-{order.pointsSpent.toLocaleString()} ₽</PriceValue>
                      </PriceRow>
                    )}
                    
                    {order.deliveryCost > 0 && (
                      <PriceRow>
                        <PriceLabel>Доставка</PriceLabel>
                        <PriceValue>{order.deliveryCost.toLocaleString()} ₽</PriceValue>
                      </PriceRow>
                    )}
                    
                    {order.deliveryCost === 0 && !order.isPickup && (
                      <PriceRow>
                        <PriceLabel>Доставка</PriceLabel>
                        <PriceValue color="#27ae60">Бесплатно</PriceValue>
                      </PriceRow>
                    )}
                    
                    <PriceRow>
                      <PriceLabel>Итого оплачено</PriceLabel>
                      <PriceValue bold color="#27ae60">{order.totalAmount.toLocaleString()} ₽</PriceValue>
                    </PriceRow>
                  </PriceSummary>
                  
                  <ToggleButton onClick={() => toggleOrderExpansion(order.id)}>
                    {expandedOrders.has(order.id) ? (
                      <>
                        <FaChevronUp />
                        Скрыть товары
                      </>
                    ) : (
                      <>
                        <FaChevronDown />
                        Показать товары
                      </>
                    )}
                  </ToggleButton>
                  
                  {expandedOrders.has(order.id) && (
                    <ProductsList>
                      {order.products && order.products.length > 0 ? (
                        order.products.map((product, index) => (
                          <ProductItem key={index}>
                            <ProductName>
                              {product.name}
                              <ProductQuantity>×{product.quantity}</ProductQuantity>
                            </ProductName>
                            <ProductPrice>{product.price.toLocaleString()} ₽</ProductPrice>
                          </ProductItem>
                        ))
                      ) : (
                        <div style={{ textAlign: 'center', color: '#95a5a6', padding: '20px' }}>
                          Информация о товарах недоступна
                        </div>
                      )}
                    </ProductsList>
                  )}
                </OrderBlock>
              );
            })}
            
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24, flexWrap: 'wrap', gap: '4px' }}>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    style={{
                      margin: '0 2px',
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: '1px solid #7bbf3f',
                      background: page === i + 1 ? '#7bbf3f' : '#fff',
                      color: page === i + 1 ? '#fff' : '#7bbf3f',
                      fontWeight: 600,
                      cursor: 'pointer',
                      minWidth: 36,
                      fontSize: '14px'
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <h2>У вас пока нет заказов</h2>
            <p style={{ color: '#666', marginTop: '10px' }}>
              Совершите первую покупку, чтобы увидеть историю заказов
            </p>
          </div>
        )}
      </Container>
      <DashboardFooter />
    </>
  );
};

export default OrdersHistory; 